// ─── ProfilePage.jsx ────────────────────────────────────────────────────────
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, Card, CardContent, Grid, TextField, Button, Avatar, Divider, Alert, CircularProgress } from '@mui/material';
import { Save, Lock } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { authAPI } from '../services/api';
import { fetchProfile } from '../store/slices/authSlice';
import { toast } from 'react-toastify';

export const ProfilePage = () => {
  const { user } = useSelector(s => s.auth);
  const dispatch = useDispatch();
  const { register, handleSubmit, formState: { errors } } = useForm({ defaultValues: user });
  const { register: regPwd, handleSubmit: handlePwd, formState: { errors: pwdErrors }, reset: resetPwd } = useForm();
  const [saving, setSaving] = useState(false); const [changingPwd, setChangingPwd] = useState(false);

  const onSave = async (data) => {
    try { setSaving(true); await authAPI.updateProfile(data); dispatch(fetchProfile()); toast.success('Profile updated'); }
    catch { toast.error('Failed to update profile'); } finally { setSaving(false); }
  };
  const onChangePwd = async (data) => {
    try { setChangingPwd(true); await authAPI.changePassword(data); toast.success('Password changed'); resetPwd(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed to change password'); } finally { setChangingPwd(false); }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>My Profile</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ width: 72, height: 72, fontSize: '1.5rem', bgcolor: 'primary.main' }}>
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{user?.first_name} {user?.last_name}</Typography>
                  <Typography color="text.secondary">{user?.email}</Typography>
                  <Typography variant="caption">{(user?.roles||[]).join(', ')}</Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <form onSubmit={handleSubmit(onSave)}>
                <Grid container spacing={2}>
                  <Grid item xs={6}><TextField fullWidth label="First Name" {...register('first_name', { required: true })} /></Grid>
                  <Grid item xs={6}><TextField fullWidth label="Last Name" {...register('last_name', { required: true })} /></Grid>
                  <Grid item xs={12}><TextField fullWidth label="Phone" {...register('phone')} /></Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="Language" {...register('preferred_language')} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="Currency" {...register('preferred_currency')} />
                  </Grid>
                  <Grid item xs={12}>
                    <Button type="submit" variant="contained" startIcon={<Save />} disabled={saving}>
                      {saving ? <CircularProgress size={20} /> : 'Save Profile'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Lock /> Change Password
              </Typography>
              <form onSubmit={handlePwd(onChangePwd)}>
                <Grid container spacing={2}>
                  <Grid item xs={12}><TextField fullWidth label="Current Password" type="password" {...regPwd('current_password', { required: true })} error={!!pwdErrors.current_password} /></Grid>
                  <Grid item xs={12}><TextField fullWidth label="New Password" type="password" {...regPwd('new_password', { required: true, minLength: { value: 8, message: 'Min 8 characters' } })} error={!!pwdErrors.new_password} helperText={pwdErrors.new_password?.message} /></Grid>
                  <Grid item xs={12}>
                    <Button type="submit" variant="outlined" startIcon={<Lock />} disabled={changingPwd}>
                      {changingPwd ? <CircularProgress size={20} /> : 'Change Password'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
export default ProfilePage;
